import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { "data-testid": "ds-attendance-lista-card" }
const _hoisted_2 = { class: "flex flex-col gap-2 py-2" }
const _hoisted_3 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_4 = { class: "text-[#52526F] text-xs font-bold leading-[14px] tracking-[0.06px]" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h4", { class: "text-textPrimary text-md font-bold" }, "Solicitação de Registro de Atividade", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", {
  class: "inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-neutral50 whitespace-nowrap",
  "data-testid": "ds-attendance-request-status"
}, " Aguardando envio ", -1)
const _hoisted_7 = {
  key: 0,
  class: "inline-flex items-center rounded-full border border-negative bg-red-50 px-2 py-0.5 text-[10px] font-bold text-negative whitespace-nowrap",
  "data-testid": "ds-attendance-request-overdue"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("span", { class: "sr-only" }, " — o prazo da política passou. A solicitação continua aberta: vencer não bloqueia aprovação, edição nem pagamento. ", -1)
const _hoisted_9 = {
  key: 1,
  class: "inline-flex items-center rounded-full border border-alert bg-[#FDF1DF] px-2 py-0.5 text-[10px] font-bold text-alert whitespace-nowrap",
  "data-testid": "ds-attendance-request-due"
}
const _hoisted_10 = { class: "text-sm text-textPrimary flex flex-col gap-0.5" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { "data-testid": "ds-attendance-request-leg" }
const _hoisted_13 = { "data-testid": "ds-attendance-request-channel" }
const _hoisted_14 = {
  key: 1,
  "data-testid": "ds-attendance-request-deadline"
}
const _hoisted_15 = {
  key: 2,
  "data-testid": "ds-attendance-request-opened-at"
}
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs text-neutral50" }, " Cancelada sozinha se chegar folha aprovada, check tardio do app, App Externo ou envio manual que cubra a perna. ", -1)
const _hoisted_17 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_18 = { class: "ml-auto flex flex-wrap items-center gap-2" }

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { PropType } from 'vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  ACTIVITY_REQUEST_CHANNEL_LABELS,
  ActivityRequestChannel,
  REQUESTED_LEG_LABELS
} from '@/constants/ds-attendance.constants'
import { DEFAULT_UTC_OFFSET, formatDateTimeWithOffset } from '@/utils/date'
import { toDDMMYYYY } from '../utils/activity-time'

const REFRESH_INTERVAL_MS = 60 * 1000

export default /*#__PURE__*/_defineComponent({
  __name: 'DsAttendanceRequestCard',
  props: {
  attendanceId: {
    type: [Number, String],
    required: true
  },
  /** `yyyy-MM-dd` do plantão cobrado (o `referenceDate` do container). */
  date: { type: String, default: '' },
  unit: { type: String, default: '' },
  schedule: { type: String, default: '' },
  shifts: { type: String, default: '' },
  doctorName: { type: String as () => string | null, default: null },
  doctorCrm: { type: String as () => string | null, default: null },
  /** Vocabulário de `CoveredLegs`; valor desconhecido aparece cru em vez de sumir. */
  requestedLeg: { type: String as () => string | null, default: null },
  requestChannel: { type: String as () => string | null, default: null },
  /**
   * Prazo da cobrança. A F3 não criou campo de vencimento próprio: é o
   * `expectedSubmissionAt` do container (§6). `null` = backend anterior à F3 ou
   * solicitação sem prazo — o chip some, o card continua de pé.
   */
  expectedSubmissionAt: { type: String as () => string | null, default: null },
  /** Quando o job abriu a cobrança. Ausente em backend antigo: a linha some. */
  createdAt: { type: String as () => string | null, default: null },
  /**
   * Offset do plantão (`-03:00`), para ler `createdAt` — que é instante UTC, não parede.
   * A listagem ainda não serve o campo; sem ele o fallback é Brasília, o mesmo default de
   * `@/utils/date` e do `DsAttendanceConciliationModal`.
   */
  timezone: { type: String as () => string | null, default: null },
  /**
   * §6.5 item 3 — vencimento decidido pelo SERVIDOR (`isOverdue` do `by-company`). `null` =
   * backend sem o campo: cai no cálculo local abaixo. `default: null` evita o cast de
   * Boolean ausente para `false`.
   */
  isOverdue: { type: Boolean as PropType<boolean | null>, default: null }
},
  emits: ["details", "sendRecord"],
  setup(__props) {

const props = __props;





const formattedDate = computed(() => toDDMMYYYY(props.date) || props.date)

/** Offset do plantão; Brasília quando a listagem ainda não o serve. */
const stationOffset = computed(() => props.timezone ?? DEFAULT_UTC_OFFSET)

/*
 * R2-I4 — o prazo da solicitação chega da API como INSTANTE certo (`13:00Z` para um prazo de
 * 10:00 em Brasília: o backend grava a parede e roda em `America/Sao_Paulo`). Lê-lo
 * literalmente, como o `toWallClockLabel` fazia, mostrava 13:00 — 3 h à frente. É lido no
 * fuso do plantão, o mesmo que o card da fila usa para o declarado (regra BF7).
 */
const formattedDeadline = computed(() =>
  props.expectedSubmissionAt
    ? formatDateTimeWithOffset(props.expectedSubmissionAt, stationOffset.value)
    : ''
)

/*
 * `createdAt` (N1): `created_at` é `@CreateDateColumn` do `BaseModel`, um instante REAL em
 * UTC. Lido como parede, uma cobrança aberta às 18:00 em Brasília era anunciada como 21:00.
 * Aqui vale o offset do plantão, como `DsAttendanceConciliationModal` e
 * `StationActivityRecordsSection` já fazem.
 */
const formattedCreatedAt = computed(() =>
  props.createdAt ? formatDateTimeWithOffset(props.createdAt, stationOffset.value) : ''
)

const doctorLabel = computed(() => {
  if (!props.doctorName) return ''
  return props.doctorCrm ? `${props.doctorName} (CRM ${props.doctorCrm})` : props.doctorName
})

/** Catálogo local primeiro; código fora dele aparece cru — chip vazio esconderia a cobrança. */
const requestedLegLabel = computed(
  () =>
    (props.requestedLeg ? REQUESTED_LEG_LABELS[props.requestedLeg] : null) ??
    props.requestedLeg ??
    'Perna não informada'
)

const requestChannelLabel = computed(
  () =>
    ACTIVITY_REQUEST_CHANNEL_LABELS[props.requestChannel as ActivityRequestChannel] ??
    props.requestChannel ??
    'Canal não informado'
)

/**
 * Relógio REATIVO. `Date.now()` dentro de um `computed` não cria dependência nenhuma: um card
 * montado ficava congelado em "Vence em 2 h" e nunca virava "Vencida" enquanto a fila
 * estivesse aberta — e a fila fica aberta o turno inteiro.
 *
 * Um minuto é a menor granularidade que o card mostra (horas e dias), então é o passo certo.
 */
const now = ref(Date.now())
let refreshTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  refreshTimer = setInterval(() => {
    now.value = Date.now()
  }, REFRESH_INTERVAL_MS)
})

onBeforeUnmount(() => {
  if (refreshTimer !== null) clearInterval(refreshTimer)
  refreshTimer = null
})

/** Milissegundos até o prazo (instante real); `null` quando não há prazo legível. */
const millisecondsToDeadline = computed<number | null>(() => {
  if (!props.expectedSubmissionAt) return null
  const deadline = new Date(props.expectedSubmissionAt).getTime()
  return Number.isNaN(deadline) ? null : deadline - now.value
})

const isOverdue = computed(() => {
  if (props.isOverdue !== null) return props.isOverdue
  return millisecondsToDeadline.value !== null && millisecondsToDeadline.value <= 0
})

/**
 * Até 48 h a conta é em horas: "Vence em 1 dia" para algo que vence em 90 minutos esconde
 * a urgência de uma cobrança cujo prazo padrão do App Minha Escala é de 2 h (Q9).
 */
const dueLabel = computed(() => {
  const remaining = millisecondsToDeadline.value
  if (remaining === null || remaining <= 0) return ''
  const hours = Math.ceil(remaining / (60 * 60 * 1000))
  if (hours <= 48) return `Vence em ${hours} h`
  const days = Math.ceil(hours / 24)
  return `Vence em ${days} ${days === 1 ? 'dia' : 'dias'}`
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["border rounded-lg p-4 py-2 mb-4", isOverdue.value ? 'border-negative bg-red-50' : 'border-alert bg-orange-50']),
      "data-testid": "ds-attendance-request-card"
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("span", _hoisted_4, "#" + _toDisplayString(__props.attendanceId), 1),
          _hoisted_5,
          _hoisted_6,
          (isOverdue.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_7, [
                _createTextVNode(" Vencida "),
                _hoisted_8
              ]))
            : (dueLabel.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(dueLabel.value), 1))
              : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_10, [
          (doctorLabel.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                _createElementVNode("b", null, _toDisplayString(doctorLabel.value), 1)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("span", null, [
            _createTextVNode(" Plantão "),
            _createElementVNode("b", null, _toDisplayString(formattedDate.value), 1),
            _createTextVNode(" · " + _toDisplayString(__props.unit || 'Unidade não informada') + " · " + _toDisplayString(__props.schedule || 'Escala não informada') + " ", 1),
            (__props.shifts)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(" · " + _toDisplayString(__props.shifts), 1)
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("span", _hoisted_12, [
            _createTextVNode(" Perna cobrada: "),
            _createElementVNode("b", null, _toDisplayString(requestedLegLabel.value), 1)
          ]),
          _createElementVNode("span", _hoisted_13, [
            _createTextVNode(" Canal esperado: "),
            _createElementVNode("b", null, _toDisplayString(requestChannelLabel.value), 1)
          ]),
          (formattedDeadline.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_14, [
                _createTextVNode(" Prazo da política: "),
                _createElementVNode("b", null, "até " + _toDisplayString(formattedDeadline.value), 1)
              ]))
            : _createCommentVNode("", true),
          (formattedCreatedAt.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_15, [
                _createTextVNode(" Aberta automaticamente em "),
                _createElementVNode("b", null, _toDisplayString(formattedCreatedAt.value), 1)
              ]))
            : _createCommentVNode("", true)
        ]),
        _hoisted_16,
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createVNode(OutlinedButton, {
              "data-testid": "ds-attendance-request-send-record",
              text: "Enviar registro",
              width: "150px",
              height: "32px",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('sendRecord', props.attendanceId)))
            }),
            _createVNode(OutlinedButton, {
              "data-testid": "ds-attendance-request-details",
              text: "Ver detalhes",
              width: "130px",
              height: "32px",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('details', props.attendanceId)))
            })
          ])
        ])
      ])
    ], 2)
  ]))
}
}

})